import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "../css/CartDrawer.css";
import { API_BASE_URL } from "../config/constants";
import {
  getGuestCart,
  updateGuestCartQty,
  removeGuestCartItem,
} from "../Utils/guestCart";
import { isTokenExpired } from "../Utils/authHelpers";
import { getProduct } from "../Utils/productCache";

export default function CartDrawer() {
  const [isOpen, setIsOpen] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const navigate = useNavigate();

  const api = axios.create({
    baseURL: API_BASE_URL,
    withCredentials: true,
  });

  api.interceptors.request.use((config) => {
    const token = localStorage.getItem("token");
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  });

  const fetchCart = useCallback(async () => {
    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("userId");
    const loggedIn = userId && token && !isTokenExpired(token);

    setLoading(true);

    if (!loggedIn) {
      const guestItems = getGuestCart();
      try {
        const enriched = await Promise.all(
          guestItems.map(async (item) => {
            let img = item.productImage;
            let name = item.productName;
            let price = item.productPrice;

            if (!img || !price || name === "Product") {
              try {
                const prod = await getProduct(item.productId, token);
                if (prod) {
                  name = prod.productName || name;
                  price = prod.productPrice ?? price;
                  const imgs = Array.isArray(prod.productImages) ? prod.productImages : [];
                  const valid = imgs.find(
                    (i) => typeof i === "string" && !i.endsWith(".mp4") && !i.endsWith(".webm")
                  );
                  img = valid || imgs[0] || img;
                }
              } catch (e) {
                // Ignore fallback
              }
            }

            return {
              cartId: null,
              productId: item.productId,
              productName: name || "Product",
              productImage: img || "",
              productPrice: Number(price) || 0,
              cartSize: item.size,
              cartQty: item.quantity || 1,
            };
          })
        );
        setCartItems(enriched);
      } catch {
        setCartItems(
          guestItems.map((item) => ({
            cartId: null,
            productId: item.productId,
            productName: item.productName || "Product",
            productImage: item.productImage || "",
            productPrice: Number(item.productPrice) || 0,
            cartSize: item.size,
            cartQty: item.quantity || 1,
          }))
        );
      } finally {
        setLoading(false);
      }
      return;
    }

    try {
      const res = await api.get(`/cart/user/${userId}`);
      const raw = res.data?.data ?? res.data;
      const items = Array.isArray(raw) ? raw : [];

      const enriched = await Promise.all(
        items.map(async (item) => {
          let name = item.product?.productName || item.productName;
          let price = item.product?.productPrice ?? item.productPrice;
          let img = item.product?.productImages?.[0] || item.productImage;

          try {
            const prod = await getProduct(item.productId, token);
            if (prod) {
              name = prod.productName || name;
              price = prod.productPrice ?? price;
              const imgs = Array.isArray(prod.productImages) ? prod.productImages : [];
              const valid = imgs.find(
                (i) => typeof i === "string" && !i.endsWith(".mp4") && !i.endsWith(".webm")
              );
              img = valid || imgs[0] || img;
            }
          } catch (e) {
            // Keep existing values
          }

          return {
            cartId: item.cartId,
            productId: item.productId,
            productName: name || "Product",
            productImage: img || "",
            productPrice: Number(price) || 0,
            cartSize: item.cartSize,
            cartQty: item.cartQty || 1,
          };
        })
      );

      setCartItems(enriched);
    } catch (err) {
      console.warn("Failed to fetch user cart:", err);
      const guestItems = getGuestCart();
      setCartItems(
        guestItems.map((item) => ({
          cartId: null,
          productId: item.productId,
          productName: item.productName || "Product",
          productImage: item.productImage || "",
          productPrice: Number(item.productPrice) || 0,
          cartSize: item.size,
          cartQty: item.quantity || 1,
        }))
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCart();

    const handleOpen = () => {
      fetchCart();
      setIsOpen(true);
    };

    const handleGuestUpdated = () => {
      fetchCart();
    };

    window.addEventListener("aminii-open-cart-drawer", handleOpen);
    window.addEventListener("aminii-guest-cart-updated", handleGuestUpdated);

    return () => {
      window.removeEventListener("aminii-open-cart-drawer", handleOpen);
      window.removeEventListener("aminii-guest-cart-updated", handleGuestUpdated);
    };
  }, [fetchCart]);

  // Lock background scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleUpdateQty = async (item, delta) => {
    if (isUpdating) return;
    setIsUpdating(true);

    const newQty = item.cartQty + delta;
    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("userId");
    const loggedIn = userId && token && !isTokenExpired(token);

    if (!loggedIn) {
      if (newQty <= 0) {
        removeGuestCartItem(item.productId, item.cartSize);
      } else {
        updateGuestCartQty(item.productId, item.cartSize, newQty);
      }
      await fetchCart();
      setIsUpdating(false);
      return;
    }

    try {
      if (newQty <= 0) {
        await api.delete(`/cart/${item.cartId}`);
      } else {
        await api.put(`/cart/${item.cartId}`, {
          userId: parseInt(userId, 10),
          productId: parseInt(item.productId, 10),
          cartSize: item.cartSize,
          cartQty: newQty,
        });
      }
      await fetchCart();
    } catch (err) {
      console.warn("Failed to update cart quantity:", err);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleRemoveItem = async (item) => {
    if (isUpdating) return;
    setIsUpdating(true);

    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("userId");
    const loggedIn = userId && token && !isTokenExpired(token);

    if (!loggedIn) {
      removeGuestCartItem(item.productId, item.cartSize);
      await fetchCart();
      setIsUpdating(false);
      return;
    }

    try {
      await api.delete(`/cart/${item.cartId}`);
      await fetchCart();
    } catch (err) {
      console.warn("Failed to remove item from cart:", err);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCheckoutClick = () => {
    setIsOpen(false);
    navigate("/checkout");
  };

  const subtotal = cartItems.reduce(
    (sum, item) => sum + (item.productPrice || 0) * (item.cartQty || 1),
    0
  );

  return (
    <>
      <div
        className={`cart-drawer-overlay ${isOpen ? "open" : ""}`}
        onClick={() => setIsOpen(false)}
        aria-hidden="true"
      />
      <div
        className={`cart-drawer ${isOpen ? "open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label="Shopping Cart"
      >
        <div className="cart-drawer-header">
          <h2 className="cart-drawer-title">Cart</h2>
          <button
            type="button"
            className="cart-drawer-close"
            onClick={() => setIsOpen(false)}
            aria-label="Close cart drawer"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="cart-drawer-body">
          {loading && cartItems.length === 0 ? (
            <div className="cart-drawer-empty">
              <p className="cart-drawer-empty-text">Loading cart...</p>
            </div>
          ) : cartItems.length === 0 ? (
            <div className="cart-drawer-empty">
              <svg
                className="cart-drawer-empty-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
                <line x1="3" y1="6" x2="21" y2="6" />
                <path d="M16 10a4 4 0 0 1-8 0" />
              </svg>
              <p className="cart-drawer-empty-text">Your cart is currently empty.</p>
              <button
                type="button"
                className="cart-drawer-continue-btn"
                onClick={() => {
                  setIsOpen(false);
                  navigate("/productList");
                }}
              >
                Continue Shopping
              </button>
            </div>
          ) : (
            <ul className="cart-drawer-items">
              {cartItems.map((item) => {
                const key = item.cartId ?? `${item.productId}-${item.cartSize}`;
                return (
                  <li key={key} className="cart-drawer-item">
                    <img
                      src={
                        item.productImage ||
                        "https://placehold.co/100x130?text=Aminii"
                      }
                      alt={item.productName}
                      className="cart-drawer-item-img"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "https://placehold.co/100x130?text=Aminii";
                      }}
                    />
                    <div className="cart-drawer-item-details">
                      <h4 className="cart-drawer-item-name">{item.productName}</h4>
                      {item.cartSize && (
                        <p className="cart-drawer-item-size">{item.cartSize}</p>
                      )}
                      <div className="cart-drawer-item-bottom">
                        <div className="cart-drawer-qty-control">
                          <button
                            type="button"
                            className="cart-drawer-qty-btn"
                            onClick={() => handleUpdateQty(item, -1)}
                            disabled={isUpdating}
                            aria-label="Decrease quantity"
                          >
                            –
                          </button>
                          <span className="cart-drawer-qty-val">{item.cartQty}</span>
                          <button
                            type="button"
                            className="cart-drawer-qty-btn"
                            onClick={() => handleUpdateQty(item, 1)}
                            disabled={isUpdating}
                            aria-label="Increase quantity"
                          >
                            +
                          </button>
                        </div>
                        <div className="cart-drawer-item-price">
                          ₹{Number(item.productPrice * item.cartQty).toLocaleString("en-IN")}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="cart-drawer-item-remove"
                        onClick={() => handleRemoveItem(item)}
                        disabled={isUpdating}
                      >
                        Remove
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {cartItems.length > 0 && (
          <div className="cart-drawer-footer">
            <div className="cart-drawer-subtotal-row">
              <span className="cart-drawer-subtotal-label">Subtotal</span>
              <span className="cart-drawer-subtotal-val">
                ₹{Number(subtotal).toLocaleString("en-IN")} INR
              </span>
            </div>
            <p className="cart-drawer-notice">
              Shipping, taxes, and discounts codes calculated at checkout.
            </p>
            <button
              type="button"
              className="cart-drawer-checkout-btn"
              onClick={handleCheckoutClick}
            >
              Check Out
            </button>
          </div>
        )}
      </div>
    </>
  );
}
